
import React from 'react';
import { Trophy, Timer } from 'lucide-react';
import { CardContent } from '@/components/ui/card';
import { useTranslation } from "react-i18next";
import { useIsMobile } from '@/hooks/use-mobile';
import { EnhancedPropBotStatsService } from '@/services/prop-bot/enhanced-prop-bot-stats-service';

interface PropBotStatsSectionProps {
  passRate?: number;
  estimatedPassTime?: number;
  connectedAccountsTotal?: number;
  isInstantAccount?: boolean;
  winRate?: string;
  potentialProfit?: string;
  // Enhanced stats props
  enhancedStats?: {
    winRate: number;
    maxDrawdown: number;
    potentialProfit: number;
    passRate: number;
    estimatedPassTime: number;
    connectedAccountsTotal: number;
    dataSource: {
      winRate: 'growth_chart' | 'manual';
      maxDrawdown: 'growth_chart' | 'manual';
      potentialProfit: 'growth_chart' | 'manual';
    };
  };
}

export const PropBotStatsSection = ({
  passRate,
  estimatedPassTime,
  connectedAccountsTotal = 0,
  isInstantAccount = false,
  winRate,
  potentialProfit,
  enhancedStats
}: PropBotStatsSectionProps) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  // Use enhanced stats if available, fallback to manual props
  const displayWinRate = enhancedStats?.winRate ? `${enhancedStats.winRate}%` : (winRate || t('not_available'));
  const displayPotentialProfit = enhancedStats?.potentialProfit ? `${enhancedStats.potentialProfit}%` : (potentialProfit || t('not_available'));
  const displayPassRate = enhancedStats?.passRate || passRate;
  const displayEstimatedPassTime = enhancedStats?.estimatedPassTime || estimatedPassTime;
  const displayConnectedAccounts = enhancedStats?.connectedAccountsTotal || connectedAccountsTotal;

  // Determine profit label based on enhanced stats (Growth Chart data = Total Profit)
  const profitLabel = enhancedStats?.dataSource?.potentialProfit === 'growth_chart' ? t('total_profit') : t('potential_profit');

  if (isInstantAccount) {
    return (
      <CardContent className={`${isMobile ? "py-3 px-4" : "py-4"} space-y-4`}>
        <div className="grid grid-cols-2 gap-4 text-center">
          <div className="flex flex-col items-center space-y-1 p-3 rounded-lg bg-gradient-to-br from-amber-500/20 to-yellow-600/20 border border-amber-500/30">
            <Trophy className={`${isMobile ? "h-4 w-4" : "h-5 w-5"} text-amber-500`} />
            <div className={`${isMobile ? "text-[10px]" : "text-xs"} text-muted-foreground`}>{t('win_rate')}</div>
            <div className={`${isMobile ? "text-xl" : "text-2xl"} font-bold text-amber-500`}>
              {displayWinRate}
            </div>
          </div>
          
          <div className="flex flex-col items-center space-y-1 p-3 rounded-lg bg-gradient-to-br from-emerald-500/20 to-green-600/20 border border-emerald-500/30">
            <Timer className={`${isMobile ? "h-4 w-4" : "h-5 w-5"} text-emerald-500`} />
            <div className={`${isMobile ? "text-[10px]" : "text-xs"} text-muted-foreground`}>{profitLabel}</div>
            <div className={`${isMobile ? "text-xl" : "text-2xl"} font-bold text-emerald-500`}>
              {displayPotentialProfit}
            </div>
          </div>
        </div>

        {displayConnectedAccounts > 0 && (
          <div className={`text-center ${isMobile ? "text-xs" : "text-sm"} text-muted-foreground`}>
            {displayConnectedAccounts} {t('connected_accounts')}
          </div>
        )}
      </CardContent>
    );
  }

  return (
    <CardContent className={`${isMobile ? "py-3 px-4" : "py-4"} space-y-4`}>
      <div className="grid grid-cols-2 gap-4 text-center">
        <div className="flex flex-col items-center space-y-1 p-3 rounded-lg bg-emerald-500/10">
          <Trophy className={`${isMobile ? "h-4 w-4" : "h-5 w-5"} text-emerald-500`} />
          <div className={`${isMobile ? "text-[10px]" : "text-xs"} text-muted-foreground`}>{t('pass_rate')}</div>
          <div className={`${isMobile ? "text-xl" : "text-2xl"} font-bold text-emerald-500`}>
            {displayPassRate ? `${displayPassRate}%` : t('not_available')}
          </div>
        </div>
        
        <div className="flex flex-col items-center space-y-1 p-3 rounded-lg bg-blue-500/10">
          <Timer className={`${isMobile ? "h-4 w-4" : "h-5 w-5"} text-blue-500`} />
          <div className={`${isMobile ? "text-[10px]" : "text-xs"} text-muted-foreground`}>{t('est_pass_time')}</div>
          <div className={`${isMobile ? "text-xl" : "text-2xl"} font-bold text-blue-500`}>
            {displayEstimatedPassTime ? `${displayEstimatedPassTime} ${t('days')}` : t('not_available')}
          </div>
        </div>
      </div>

      {displayConnectedAccounts > 0 && (
        <div className={`text-center ${isMobile ? "text-xs" : "text-sm"} text-muted-foreground`}>
          {displayConnectedAccounts} {t('connected_accounts')}
        </div>
      )}
    </CardContent>
  );
};
